import "./styles.css";
import { el, clear } from "./util/dom";
import { buildDashboardView } from "./ui/dashboardView";
import { buildChartView } from "./ui/chartView";
import { buildAlertsView } from "./ui/alertsView";
import { buildAuthView } from "./ui/authView";
import { buildRegisterView } from "./ui/registerView";
import { buildFeedbackView } from "./ui/feedbackView";
import { buildAdminView } from "./ui/adminView";
import { buildVolumeHistoryView } from "./ui/volumeHistoryView";
import { buildNotificationCenter } from "./ui/notifications";
import { buildOpenInAppPill } from "./ui/openInApp";
import { isWalkthroughDone, showWalkthrough } from "./ui/walkthrough";
import { AlertEvaluator } from "./alerts/evaluator";
import { probeStream, streamUrl, subscribeTickStream } from "./api/stream";
import { getDailyExtremes, get24hStats, listAlerts } from "./api/endpoints";
import { DEFAULT_BAND_WIDTH_USD } from "./api/types";
import type { AlertResponse, Tick } from "./api/types";
import { getStoredToken, isLoggedIn } from "./api/auth";
import { isAdminAccount } from "./api/admin";
import { pingFunnel } from "./api/funnel";
import { installClientErrorCapture, recordClientError } from "./clientErrors";
import type { StreamHandle, StreamStatus } from "./api/stream";

// First statement: tells public/boot-check.js the module parsed and ran.
document.documentElement.setAttribute("data-booted", "");

const app = document.getElementById("app");
if (!app) throw new Error("missing #app root element");

const errorBanner = el("div", { class: "error-banner" });
errorBanner.style.display = "none";
let errorTimer: number | undefined;
installClientErrorCapture();

function showError(message: string): void {
  recordClientError(message);
  errorBanner.textContent = message;
  errorBanner.style.display = "";
  window.clearTimeout(errorTimer);
  errorTimer = window.setTimeout(() => {
    errorBanner.style.display = "none";
  }, 8000);
}

const dashboard = buildDashboardView();
const chart = buildChartView(showError);
// Research bands (±$10k 2026-09-03, ±$5k 2026-10-23) were removed 2026-11-03
// (Owner); the full-book band 1e9 is still ingested, never displayed.
const alerts = buildAlertsView(showError);
// SW-23: the Stripe return (?checkout=...) and the pricing-page handoff
// (?next=subscribe) both land on the Account screen, where authView reads
// (and strips) those params. Rewrite the path before the view is built.
const bootParams = new URLSearchParams(window.location.search);
if (bootParams.has("checkout") || bootParams.get("next") === "subscribe") {
  window.history.replaceState({}, "", `/account${window.location.search}`);
}
const auth = buildAuthView(showError);
const volumeHistory = buildVolumeHistoryView(showError);
const notificationCenter = buildNotificationCenter();

// Re-render the history chart and the alerts panel (list values + entry hint)
// when the user switches raw <-> Normalized scale.
// (The dashboard's ratio readouts re-render synchronously inside the view.)
dashboard.onScaleChange(() => {
  void chart.refresh();
  alerts.rerender();
});

function onAuthChange(loggedIn: boolean): void {
  alerts.setVisible(loggedIn);
  if (loggedIn) void alerts.refresh();
  void chart.refresh(); // re-check entitlement immediately on login/logout
  void pollAlertsForNotifier(); // pick up the new/cleared alert list immediately
  // ADR-0013: the SSE connection's token is fixed at construction
  // (EventSource can't change its URL), so reconnect with/without the
  // stored token, and re-poll daily extremes with the new auth state --
  // both flip the gated slots (R-HOD/R-LOD/depths) between live values and
  // "Subscribers Only" without a page reload.
  mainStream.connect();
  void pollDailyExtremes();
  // SW-29: the contact field hides once the API can stamp the account; the
  // ADMIN menu row is re-checked on every login/logout.
  feedback.setSignedIn(loggedIn);
  void syncAdminMenu();
}
auth.onAuthChange(onAuthChange);
// SW-24: a successful registration stores the token; refresh the account
// screen into its logged-in state (this also consumes a pending
// ?next=subscribe handoff) and show it.
const registerView = buildRegisterView(() => {
  onAuthChange(true);
  void auth.refresh().then(() => navigate("/account"));
});

// Plain block wrapper around the readout.
const readoutRow = el("div", { class: "readout-row" }, [dashboard.root]);

// SW-23: three screens, mirroring the mobile apps' MENU / ACCOUNT split.
// The readout is the landing view at `/`; MENU (`/menu`) lists Alerts and
// the Account row; ACCOUNT (`/account`) is its own screen. Real paths, not
// hashes: the edge serves `try_files {path} /index.html`, so a deep link to
// /account loads this same bundle. Views are built once and swapped in and
// out of `viewSlot`, so every widget (streams, chart, alerts) keeps its state and timers while another screen is showing.
const homeView = el("div", {}, [
  readoutRow,
  el("div", { class: "divider" }),
  chart.root,
  volumeHistory.root,
]);
// SW-29: FEEDBACK for everyone; ADMIN only once GET /v1/admin/stats says so
// (syncAdminMenu). The admin screens are fetched fresh on every show.
const feedback = buildFeedbackView();
const admin = buildAdminView(navigate);
function menuRow(label: string, href: string): HTMLElement {
  return el("a", { class: "menu-row", href, "data-route": "" }, [
    label,
    el("span", { "aria-hidden": "true" }, ["›"]),
  ]);
}
const adminRow = menuRow("Admin", "/admin");
adminRow.style.display = "none";
// SW-31: replays the walkthrough over the clock screen. Not a routed link --
// the readout must be on screen (measured) before the overlay is placed.
const walkthroughRow = menuRow("Walkthrough", "/");
walkthroughRow.removeAttribute("data-route");
walkthroughRow.addEventListener("click", (event) => {
  event.preventDefault();
  navigate("/");
  startWalkthrough();
});
const menuView = el("div", {}, [
  el("div", { class: "section-title" }, ["Alerts"]),
  notificationCenter.audioToggle,
  alerts.root,
  el("div", { class: "section-title" }, ["Account"]),
  menuRow("Account", "/account"),
  menuRow("Feedback", "/feedback"),
  walkthroughRow,
  adminRow,
]);
const screens: Record<string, { title: string; back: string; view: HTMLElement }> = {
  "/menu": { title: "MENU", back: "/", view: menuView },
  "/account": { title: "ACCOUNT", back: "/menu", view: auth.root },
  "/register": { title: "REGISTER", back: "/account", view: registerView.root },
  "/feedback": { title: "FEEDBACK", back: "/menu", view: feedback.root },
};

async function syncAdminMenu(): Promise<void> {
  const isAdmin = await isAdminAccount();
  // Re-check login after the await: a logout mid-flight must win.
  adminRow.style.display = isAdmin && isLoggedIn() ? "" : "none";
}
const navLink = el("a", { class: "nav-btn", href: "/menu", "data-route": "" }, ["MENU"]);
const navTitle = el("div", { class: "nav-title" });
// SW-27: phones only (null on desktop); attached on the readout (`/`) only.
const openInApp = buildOpenInAppPill();
// SHARE sits with the other pills above the title (Owner 2026-09-11); readout only.
const shareBtn = dashboard.shareButton;
const navRow = el("div", { class: "nav-row" }, [navLink, shareBtn, navTitle]);
const viewSlot = el("div");
const shell = el("div", { class: "dashboard" }, [errorBanner, navRow, viewSlot]);

function renderRoute(): void {
  const screen = screens[window.location.pathname] ?? admin.screenFor(window.location.pathname);
  navLink.textContent = screen ? "‹ BACK" : "MENU";
  navLink.setAttribute("href", screen ? screen.back : "/menu");
  navTitle.textContent = screen ? screen.title : "";
  screen ? shareBtn.remove() : navLink.after(shareBtn);
  if (openInApp) screen ? openInApp.remove() : navRow.append(openInApp);
  viewSlot.replaceChildren(screen ? screen.view : homeView);
  window.scrollTo(0, 0);
}
function navigate(path: string): void {
  window.history.pushState({}, "", path);
  renderRoute();
}
window.addEventListener("popstate", renderRoute);
renderRoute();

clear(app);
app.append(shell, notificationCenter.root);

// SW-31: one card per readout field, contract order; STATUS · MENU outlines
// the stream dot and the MENU pill together. First visit to `/` only
// (localStorage "bitclock-walkthrough"); MENU › WALKTHROUGH replays it.
function startWalkthrough(): void {
  const f = dashboard.fields;
  showWalkthrough(
    [[f.ratio], [f.hod], [f.lod], [f.btc], [f.depth], [f.stats], [f.scale], [f.status, navLink]],
    shell
  );
}
if (window.location.pathname === "/" && !isWalkthroughDone()) startWalkthrough();

// In-app links are real <a href> (middle-click/open-in-new-tab keep working);
// a plain left click becomes a pushState navigation instead of a reload.
shell.addEventListener("click", (event) => {
  const link = event.target instanceof Element ? event.target.closest("a[data-route]") : null;
  if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
  event.preventDefault();
  navigate(link.getAttribute("href") ?? "/");
});

// XDIV 0c: count taps on the "Subscribers Only" locked slots (ADR-0013).
// One delegated listener covers the readout (inside `shell`); the ping carries no identity (api/funnel.ts).
shell.addEventListener("click", (event) => {
  if (event.target instanceof Element && event.target.closest(".locked-value")) {
    pingFunnel("locked_slot_tap");
  }
});

// --- Live tick stream (SSE) ---
// Fires the same edge-triggered crossing detection the real server-side
// alert-engine already does (src/alerts/evaluator.ts's docstring explains
// why this exists client-side too: server delivery is a log-only stub for
// now). Only meaningful once alertsForNotifier has ever been populated --
// listAlerts() requires auth, and an empty list is simply "nothing to
// evaluate," not an error.
let alertsForNotifier: AlertResponse[] = [];
const alertEvaluator = new AlertEvaluator();

// SW-8: EventSource never retries after a fatal HTTP close (e.g. expired
// JWT -> 401), so on the "closed" status we rebuild the stream ourselves
// with a short capped backoff. The rebuild re-reads getStoredToken(), so a
// refreshed/cleared token is picked up. A single timer handle guards
// against overlapping rebuilds; any received tick resets the backoff.
// SW-10: EventSource can't say WHY it closed, so each rebuild attempt first
// probes the stream URL with fetch (api/stream.ts probeStream) and redials
// only when that succeeds; a 429 waits max(Retry-After, backoff) instead.
//
// connect() (re)opens the stream with the CURRENT auth state (ADR-0013):
// logged-in sessions pass the stored JWT via the `?token=` query param
// (EventSource cannot set an Authorization header), which is what earns
// `entitled: true` ticks with unredacted depth fields. Called once at
// startup and again on every login/logout. One controller per band.
function tickStreamController(
  bandWidthUsd: number,
  onTick: (tick: Tick) => void,
  onStatus: (status: StreamStatus) => void
): { connect(): void; close(): void } {
  let stream: StreamHandle | null = null;
  let reconnectTimer: number | undefined;
  let reconnectDelayMs = 1000;
  let generation = 0; // bumped by close(); a probe that lands after one is stale

  function scheduleReconnect(delayMs = reconnectDelayMs): void {
    if (reconnectTimer !== undefined) return;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = undefined;
      const gen = generation;
      void probeStream(streamUrl(bandWidthUsd, getStoredToken()), reconnectDelayMs).then(
        (waitMs) => {
          if (gen !== generation) return;
          if (waitMs > 0) scheduleReconnect(waitMs);
          else connect();
        }
      );
    }, delayMs);
    reconnectDelayMs = Math.min(reconnectDelayMs * 2, 4000);
  }

  function close(): void {
    generation++;
    window.clearTimeout(reconnectTimer);
    reconnectTimer = undefined;
    stream?.close();
    stream = null;
  }

  function connect(): void {
    close();
    stream = subscribeTickStream(
      bandWidthUsd,
      (tick) => {
        reconnectDelayMs = 1000;
        onTick(tick);
      },
      (status) => {
        onStatus(status);
        if (status === "closed") scheduleReconnect();
      },
      getStoredToken()
    );
  }

  return { connect, close };
}

const mainStream = tickStreamController(
  DEFAULT_BAND_WIDTH_USD,
  (tick) => {
    dashboard.updateTick(tick);
    if (alertsForNotifier.length > 0 && tick.ratio !== null) {
      const fired = alertEvaluator.evaluateTick(
        tick.ratio,
        tick.ts_utc,
        performance.now(),
        alertsForNotifier
      );
      for (const alert of fired) {
        notificationCenter.fire(alert, tick.mid_price);
      }
    }
  },
  (status) => dashboard.setStreamStatus(status)
);

mainStream.connect();

// SW-8: on regaining network (waking from sleep, WiFi back), rebuild the
// stream and fire the pollers once immediately instead of waiting out the
// poll intervals. Rebuilding unconditionally is simpler than detecting
// whether EventSource is already retrying on its own.
window.addEventListener("online", () => {
  mainStream.connect();
  void pollDailyExtremes();
  void poll24hStats();
  void chart.refresh();
});

async function pollAlertsForNotifier(): Promise<void> {
  if (!isLoggedIn()) {
    alertsForNotifier = [];
    return;
  }
  try {
    alertsForNotifier = await listAlerts();
  } catch {
    // Transient failure -- keep evaluating against the last-known list
    // rather than going silent; a real error is already surfaced by
    // alertsView's own refresh() when the alerts panel is open.
  }
}

// --- Server-computed context values, polled independently of the stream.
// SPEC.md is explicit that HOD/LOD/24h-stats are server-computed values --
// this app never derives them client-side from the live stream, it always
// reads the server's own answer, so a client that's only been open a
// minute doesn't show a wrong "HOD" it invented from a minute of samples.
async function pollDailyExtremes(): Promise<void> {
  try {
    dashboard.updateDailyExtremes(await getDailyExtremes());
  } catch (err) {
    showError(err instanceof Error ? err.message : String(err));
  }
}
async function poll24hStats(): Promise<void> {
  try {
    dashboard.update24hStats(await get24hStats());
  } catch (err) {
    showError(err instanceof Error ? err.message : String(err));
  }
}

async function refreshVolumeHistory(): Promise<void> {
  await volumeHistory.refresh();
  dashboard.updateVolumeChange(volumeHistory.latestDayOverDayChange());
}

void pollDailyExtremes();
void poll24hStats();
void chart.refresh();
void refreshVolumeHistory();
void pollAlertsForNotifier();
void syncAdminMenu();
if (isLoggedIn()) {
  alerts.setVisible(true);
  void alerts.refresh();
  void auth.refresh();
}

setInterval(() => void pollDailyExtremes(), 10_000);
setInterval(() => void poll24hStats(), 30_000);
setInterval(() => void chart.refresh(), 60_000);
// Matches legacy's own refreshVolumeHistory interval exactly
// (dashboard.html's `setInterval(refreshVolumeHistory, 300000)`).
setInterval(() => void refreshVolumeHistory(), 300_000);
setInterval(() => void pollAlertsForNotifier(), 5_000);
